import React from "react";
import Select from "react-select";
import classNames from "classnames";
import { withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import Chip from "@material-ui/core/Chip";
import MenuItem from "@material-ui/core/MenuItem";
import CancelIcon from "@material-ui/icons/Cancel";
import { emphasize } from "@material-ui/core/styles/colorManipulator";

const styles = (theme) => ({
  root: {
    flexGrow: 1,
    height: 250,
  },
  input: {
    display: "flex",
    height: "100%",
    padding: "10px 14px !important",
  },
  valueContainer: {
    display: "flex",
    flexWrap: "wrap",
    flex: 1,
    alignItems: "center",
    overflow: "hidden",
  },
  chip: {
    margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
  },
  chipFocused: {
    backgroundColor: emphasize(
      theme.palette.type === "light"
        ? theme.palette.grey[300]
        : theme.palette.grey[700],
      0.08
    ),
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`,
  },
  singleValue: {
    fontSize: 16,
  },
  placeholder: {
    position: "absolute",
    left: 2,
    fontSize: 16,
  },
  paper: {
    position: "absolute",
    zIndex: 2,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0,
  },
  divider: {
    height: theme.spacing.unit * 2,
  },
});

function NoOptionsMessage(props) {
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      className={props.selectProps.classes.noOptionsMessage}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  const { inputValue } = props.selectProps;
  return (
    <TextField
      fullWidth
      variant="outlined"
      margin="normal"
      InputProps={{
        inputComponent,
        inputProps: {
          className: props.selectProps.classes.input,
          inputRef: props.innerRef,
          children: props.children,
          ...props.innerProps,
        },
      }}
      InputLabelProps={{
        shrink:
          props.selectProps.textFieldProps.hasValue ||
          (inputValue && inputValue.length),
      }}
      {...props.selectProps.textFieldProps}
    />
  );
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function Placeholder(props) {
  return (
    <Typography
      variant="body2"
      color="textSecondary"
      className={props.selectProps.classes.placeholder}
      {...props.innerProps}
    >
      {props.children}
    </Typography>
  );
}

function SingleValue(props) {
  return (
    <Typography
      className={props.selectProps.classes.singleValue}
      {...props.innerProps}
      variant="body2"
    >
      {props.children}
    </Typography>
  );
}

function ValueContainer(props) {
  return (
    <div className={props.selectProps.classes.valueContainer}>
      {props.children}
    </div>
  );
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={classNames(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
    />
  );
}

function Menu(props) {
  return (
    <Paper
      square
      className={props.selectProps.classes.paper}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
};

class MaterialSelectInner extends React.Component {
  state = {
    options: [],
    values: [],
  };

  selectToString(value) {
    if (!value) {
      return "";
    }
    return typeof value === "string"
      ? value
      : Array.isArray(value)
      ? value.map((res) => res.value).join(",")
      : `${value.value}`;
  }

  handleChange(newValue) {
    const { onChange, name } = this.props;
    this.setState({ values: newValue });
    const stringValue = this.selectToString(newValue);
    onChange(name, stringValue);
    this.selected = stringValue;
    setTimeout(() => this.getResults(stringValue), 100);
  }

  handleBlur() {
    const { onBlur, name } = this.props;
    onBlur(name, true);
  }

  constructor() {
    super();
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.getResults = this.getResults.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.selected = undefined;
  }

  getOptions(input) {
    if (this.props.optionsFetcher) {
      return this.props.optionsFetcher(input);
    }
    if (this.state.options.length) {
      return Promise.resolve(this.state.options);
    }
    return Promise.resolve(this.props.options);
  }

  getResults(input) {
    const props = this.props;
    const { options: currentOptions } = this.state;
    return this.getOptions(input).then((result) => {
      const options = result;
      const value = !isNaN(input) ? "" + input : props.value;
      const separatedValues = new Set(
        value.split(",").map((v) => {
          if (isNaN(+v)) {
            return v;
          }
          return +v;
        })
      );
      const alreadyAdded = new Set();
      const values = [...currentOptions, ...options].filter((option) => {
        if (alreadyAdded.has(option.value)) {
          return false;
        }
        alreadyAdded.add(option.value);
        return separatedValues.has(option.value);
      });

      this.setState({ options, values });
    });
  }

  componentDidMount() {
    const { value } = this.props;
    return this.getResults(value);
  }

  handleInputChange(inputValue) {
    if (!inputValue) {
      return inputValue;
    } else {
      this.selected = undefined;
      this.getResults(inputValue);
    }
    return inputValue;
  }

  render() {
    const { theme, classes } = this.props;

    const selectStyles = {
      input: (base) => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit",
        },
      }),
    };

    return (
      <Select
        cacheOptions
        defaultOptions
        classes={classes || {}}
        styles={selectStyles}
        isMulti={this.props.isMulti}
        textFieldProps={{
          label: this.props.label,
          hasValue: this.state.values && this.state.values.length > 0,
        }}
        name={this.props.name}
        isSearchable
        options={this.state.options}
        onChange={this.handleChange}
        onBlur={this.handleBlur}
        value={this.state.values}
        isClearable
        placeholder=""
        components={components}
        onInputChange={this.props.search ? this.handleInputChange : undefined}
      />
    );
  }
}

export const MaterialSelect = withStyles(styles, { withTheme: true })(
  MaterialSelectInner
);
